import { plural } from '../types';
import type { Catalog } from '../types';

export const en: Catalog = {
  common: {
    retry: 'Try again',
    appName: 'TELESCOPIO',
    points: '{score} pts',
    participants: plural({ one: '{count} participant', other: '{count} participants' }),
  },
  nav: {
    mainNav: 'Main navigation',
    home: 'Home',
    events: 'Events',
    howItWorks: 'How it works',
    login: 'Log in',
    register: 'Sign up',
    openMenu: 'Menu',
    languageMenu: 'Language: {code}',
    userMenu: 'User menu for {name}',
    myEvents: 'My events',
    notifications: 'Notifications',
    logout: 'Log out',
  },
  notifications: {
    title: 'Notifications',
    bell: 'Notifications',
    bellWithCount: plural({ one: 'Notifications, {count} unread', other: 'Notifications, {count} unread' }),
    announce: plural({ one: '{count} unread notification', other: '{count} unread notifications' }),
    newCount: plural({ one: '{count} new', other: '{count} new' }),
    unreadEyebrow: '{count} UNREAD',
    upToDateEyebrow: "YOU'RE ALL CAUGHT UP",
    intro: 'Everything that happens in the events you take part in or organize. They are kept for 90 days.',
    markAll: 'Mark all as read',
    markAllError: "We couldn't mark the notifications. Please try again.",
    viewAll: 'View all notifications',
    loadMore: 'Load more',
    loadingMore: 'Loading…',
    loading: 'Loading notifications…',
    loadingPanel: 'Loading…',
    loadError: "We couldn't load your notifications.",
    empty: "You're all caught up. We'll let you know here when something happens in your events.",
    unread: 'Unread',
    back: '← Back',
    tags: {
      registration: 'Registration',
      voting: 'Voting',
      results: 'Results',
      myEvents: 'My events',
    },
    actions: {
      viewEvent: 'View event',
      vote: 'Go vote',
      viewRanking: 'View ranking',
      viewRegistrations: 'View registrations',
      uploadFile: 'Upload file',
      viewMyRanking: 'View my ranking',
    },
    types: {
      participationOpened: {
        title: 'Registration is open for «{event}»',
        body: 'You can now register and upload your proposal.',
      },
      votingAssigned: {
        title: 'Voting is open in «{event}»',
        body: plural({
          one: 'We assigned you {count} proposal to rank. You have until {date}.',
          other: 'We assigned you {count} proposals to rank. You have until {date}.',
        }),
      },
      votingNoProposal: {
        title: 'Voting started in «{event}»',
        body: "You can't take part because you didn't upload a proposal.",
      },
      results: {
        title: 'The results of «{event}» are out',
        bodyPosition: 'You placed {position} of {total}.',
        bodyNoPosition: 'Take a look at the final ranking.',
      },
      cancelled: {
        title: '«{event}» was cancelled',
        body: 'The organizer cancelled the event.',
      },
      paused: {
        title: '«{event}» was paused',
        body: 'The organizer paused the event for now.',
      },
      deadlineChanged: {
        title: 'The deadline of «{event}» changed',
        bodyParticipation: 'Registration now closes on {date}.',
        bodyVoting: 'Voting now closes on {date}.',
      },
      registered: {
        title: plural({
          one: '{count} person registered for «{event}»',
          other: '{count} people registered for «{event}»',
        }),
      },
      registrationConfirmed: {
        title: 'You registered for «{event}»',
        body: 'You can now upload your proposal.',
      },
      rankingSubmitted: {
        title: 'You submitted your ranking in «{event}»',
        titleReplaced: 'You updated your ranking in «{event}»',
        body: 'You can change it until voting closes.',
      },
      fileReminder: {
        title: 'Your file is missing in «{event}»',
        body: 'Registration closes on {date}.',
      },
      voteReminder: {
        title: 'Your ranking is missing in «{event}»',
        body: 'Voting closes on {date}.',
      },
    },
  },
  language: {
    es: 'Español',
    en: 'English',
  },
  stages: {
    creation: { name: 'Creation' },
    participation: { name: 'Participation' },
    voting: { name: 'Voting' },
    results: { name: 'Results' },
  },
  notFound: {
    title: "We couldn't find this page",
    description: 'The link may be mistyped, or the event may no longer be available.',
    goToEvents: 'Go to Events',
    backHome: 'Back to home',
    documentTitle: 'Page not found · Telescopio',
  },
  footer: {
    tagline: 'Telescopio · distributed peer review',
  },
  auth: {
    layout: {
      backHome: '← Back to home',
      demoNote: 'Demo project · data may be reset',
    },
    fields: {
      email: 'Email',
      emailPlaceholder: 'name@email.com',
      password: 'Password',
      passwordPlaceholder: 'Your password',
      passwordRule: 'At least 8 characters.',
      newPasswordPlaceholder: 'At least 8 characters',
      showPassword: 'Show',
      hidePassword: 'Hide',
      fullName: 'Full name',
      fullNamePlaceholder: 'Your name',
    },
    google: {
      continue: 'Continue with Google',
      separator: 'or with your email',
      error: "We couldn't connect you with Google. Please try again.",
    },
    validation: {
      emailInvalid: 'Enter a valid email.',
      passwordRequired: 'Enter your password.',
      nameRequired: 'Enter your name.',
      passwordMin: 'Use at least 8 characters.',
      passwordMismatch: "The passwords don't match.",
      nameMin: 'Use at least 3 characters.',
      nameMax: 'Use at most 100 characters.',
    },
    login: {
      brandTitle: 'Pick up where you left your events.',
      brandTitleNext: 'Log in to continue.',
      benefitFollow: 'Follow the status of the events you take part in',
      benefitSubmit: "Upload your proposal and vote when it's your turn",
      benefitResults: 'See the full final rankings',
      title: 'Log in',
      noAccount: "Don't have an account?",
      createOne: 'Create one for free',
      forgot: 'Forgot it?',
      submit: 'Log in',
      submitting: 'Logging in…',
      error: "We couldn't log you in. Please try again.",
    },
    register: {
      brandTitle: 'Join events where the community decides.',
      benefitJoin: 'Sign up and upload your proposal',
      benefitEvaluate: 'Review other participants',
      benefitCreate: 'Create your own events',
      title: 'Sign up',
      haveAccount: 'Already have an account?',
      login: 'Log in',
      submit: 'Create account',
      submitting: 'Creating account…',
      emailTaken: "There's already an account with this email. Log in or recover your password.",
      error: "We couldn't create your account. Please try again.",
    },
    forgot: {
      brandTitle: 'Get back into your events.',
      backToLogin: '← Back to log in',
      title: 'Recover password',
      explanation: "We'll send you a link to set a new password. If you joined an event through a link and never created a password, use this option to create one.",
      submit: 'Send link',
      submitting: 'Sending…',
      sentTitle: 'Check your email',
      sentBody: "If there's an account for {email}, we've sent you a link. It expires in 1 hour. Check your spam folder too.",
      error: "We couldn't send the link. Please try again.",
    },
    reset: {
      brandTitle: "One more step and you're back to your events.",
      title: 'Set your new password',
      newPassword: 'New password',
      repeatPassword: 'Repeat the password',
      submit: 'Save password',
      submitting: 'Saving…',
      error: "We couldn't save the password. Please try again.",
      successTitle: 'Password updated',
      successBody: 'You can now log in with your new password.',
      goToLogin: 'Log in',
      invalidTitle: 'The link is invalid or has expired',
      invalidBody: 'Recovery links expire in 1 hour and work only once. Request a new one.',
      requestNew: 'Request a new link',
    },
    completeProfile: {
      brandTitle: "You're almost there.",
      title: 'Choose your name',
      explanation: "It's the name organizers see and the one shown in event results.",
      name: 'Name',
      namePlaceholder: 'At least 3 characters',
      submit: 'Continue',
      submitting: 'Saving…',
      nameTaken: 'That name is already taken. Try another one.',
      error: "We couldn't save your name. Please try again.",
    },
  },
  home: {
    eyebrow: 'Peer review · distributed voting',
    title: 'Contests where the community decides who wins.',
    subtitle: 'Create an event, receive proposals and let the participants review each other. Fair results, no central jury.',
    explore: 'Explore open events →',
    create: 'Create an event',
    featured: {
      label: 'Featured event',
      status: 'Registration open',
      closesIn: plural({ one: 'Closes in {count} day', other: 'Closes in {count} days' }),
      closesToday: 'Closes today',
      capacity: '{count} of {max} spots taken',
      participate: 'Join',
    },
    how: {
      eyebrow: 'How it works',
      title: 'Four stages, always visible',
      step: '{number} · {stage}',
      creation: 'The organizer defines the event and its capacity.',
      participation: 'You sign up and upload your proposal before the deadline.',
      voting: 'Each participant reviews and ranks other people\'s proposals.',
      results: 'The final ranking is published for everyone.',
    },
    open: {
      title: 'Open now',
      seeAll: 'See all events →',
      cta: 'View and join',
      empty: 'There are no events open for registration right now.',
      emptyAction: 'See all events',
      error: 'We couldn\'t load the open events.',
      loading: 'Loading events…',
    },
    banner: {
      title: 'Want to review proposals with your community?',
      text: 'Setting up an event takes less than 5 minutes.',
      cta: 'Create my first event',
    },
  },
  events: {
    timeline: {
      now: 'Now',
      completed: 'Completed',
      pending: 'Pending',
      stepOf: 'Stage {number} of 4',
    },
    pill: {
      participation: 'Registration open',
      voting: 'Voting',
      results: 'Finished',
      draft: 'Draft · not visible',
      paused: 'Paused',
      cancelled: 'Cancelled',
    },
    actions: {
      manage: 'Manage',
      participate: 'Join',
      upload: 'Upload file',
      view: 'View event',
      vote: 'Vote',
      results: 'View results',
      requiresAccount: 'Requires an account',
      public: 'Public',
      accessibleName: '{action} {name}',
    },
    table: {
      event: 'Event',
      stage: 'Stage',
      participants: 'Participants',
      created: 'Created',
      close: 'Closes',
      myStatus: 'My status',
      action: 'Action',
      capacity: '{count} / {max}',
      capacityText: '{count} of {max} spots taken',
      registered: plural({ one: '{count} registered', other: '{count} registered' }),
    },
    myStatus: {
      missingFile: 'Your file is missing',
      proposalSent: 'Proposal sent',
      toVote: 'Your turn to vote',
      rankingSent: 'Ranking sent',
      notVoting: 'You\'re not part of the voting',
      position: 'Place {position} of {total}',
    },
    pending: {
      title: 'Your to-dos · {count}',
      uploadTitle: 'Your file is still missing',
      uploadAction: 'Upload file',
      voteTitle: 'Your turn to vote',
      voteAction: 'Vote',
      resultsTitle: 'Results published',
      resultsPosition: 'You finished in place {position} of {total}',
      resultsAction: 'View ranking',
      closes: 'Closes on {date}',
      seeAll: 'See all in My events',
      error: 'We couldn\'t load your to-dos.',
      loading: 'Loading your to-dos…',
    },
    list: {
      eyebrowGuest: 'Explore · {count} public events',
      title: 'Events',
      subtitle: 'Find where to take part or follow the events you\'re already in.',
      subtitleGuest: 'See what\'s being reviewed and check the published results. To take part or create your own event you need a free account.',
      create: '+ Create event',
      createGuest: 'Create an event',
      filtersLabel: 'Filter by stage',
      filterAll: 'All',
      filterParticipation: 'Registration open',
      filterVoting: 'Voting',
      filterResults: 'Finished',
      filterAccessible: plural({ one: '{label}, {count} event', other: '{label}, {count} events' }),
      searchLabel: 'Search events',
      searchPlaceholder: 'Search by name or organizer',
      searchClear: 'Clear search',
      resultsCount: plural({ one: '{count} event', other: '{count} events' }),
      loading: 'Loading events…',
      noMatches: 'No events match your search.',
      clearFilters: 'Clear filters',
      noEvents: 'There are no public events yet.',
      error: 'We couldn\'t load the events.',
      paginationLabel: 'Pagination',
      previous: 'Previous',
      next: 'Next',
      pageOf: '{page} of {total}',
      howTitle: 'How to take part',
      howStep1: 'Pick an event open for registration',
      howStep2: 'Create your account and sign up',
      howStep3: 'Upload your proposal',
      howStep4: 'Vote on other participants',
      bannerTitle: 'Want your community to review proposals?',
      bannerText: 'Create a free account and set up your event in less than 5 minutes.',
      bannerCta: 'Create a free account',
    },
  },
  myEvents: {
    title: 'My events',
    subtitle: 'The events you organize and take part in, all in one place.',
    create: '+ Create event',
    organizingTitle: 'I organize · {count}',
    organizingEmpty: 'You haven\'t organized any events yet.',
    organizingEmptyAction: 'Create event',
    participatingTitle: 'I take part · {count}',
    participatingEmpty: 'You haven\'t signed up for any events yet.',
    participatingEmptyAction: 'Explore events',
    error: 'We couldn\'t load your events.',
    loading: 'Loading your events…',
  },
  eventForm: {
    nameLabel: 'Event name',
    namePlaceholder: 'E.g. Spring photo contest 2026',
    nameHelp: 'It\'s the first thing people see in the events list.',
    descriptionLabel: 'Description',
    descriptionHelp: 'Include what to submit and how it\'s evaluated.',
    organizerLabel: 'Organizer',
    optional: '(optional)',
    organizerPlaceholder: 'E.g. Photography Club',
    organizerHelp: 'If you leave it empty, your name is shown.',
    capacityLabel: 'Participant limit',
    capacityLabelShort: 'Limit',
    capacityRange: 'Between 1 and 100',
    capacityMin: plural({ one: 'Minimum {count}: {count} person is already registered.', other: 'Minimum {count}: {count} people are already registered.' }),
    capacityDecrement: 'Decrease limit',
    capacityIncrement: 'Increase limit',
    validation: {
      nameMin: 'The name must have at least 3 characters.',
      nameMax: 'The name can have up to 200 characters.',
      descriptionMin: 'The description must have at least 10 characters.',
      descriptionMax: 'The description can have up to 2000 characters.',
      organizerMax: 'It can have up to 200 characters.',
      capacityRange: 'The limit must be between 1 and 100.',
      capacityBelowRegistered: plural({ one: 'The limit can\'t be lower than the {count} registered person.', other: 'The limit can\'t be lower than the {count} registered people.' }),
    },
  },
  createEvent: {
    breadcrumbLabel: 'Breadcrumb',
    breadcrumbEvents: 'Events',
    title: 'Create event',
    stepsLabel: 'Wizard steps',
    steps: {
      identification: 'Details',
      capacity: 'Dates and capacity',
      review: 'Review and create',
    },
    stepCurrent: 'In progress',
    stepCompleted: '{name}, completed',
    stepOf: 'Step {number} of 3',
    stepAnnouncement: 'Step {number} of 3, {name}',
    questions: {
      identification: 'What is your event about?',
      capacity: 'How many people can take part?',
      review: 'Review and create your event',
    },
    capacityHelp: 'Once the limit is reached, nobody else can sign up. You can change it while registration is open.',
    review: {
      name: 'Name',
      description: 'Description',
      organizer: 'Organizer',
      capacity: 'Limit',
      capacityValue: plural({ one: '{count} participant', other: '{count} participants' }),
      organizerFallback: '{name} (your name)',
      edit: 'Edit',
      editField: 'Edit {field}',
    },
    visibilityNotice: 'The event is created in the Creation stage: nobody else can see it until you open registration. You set the closing date at that point.',
    preview: {
      title: 'Preview in the list',
      namePlaceholder: 'Event name',
      descriptionPlaceholder: 'The description will appear here.',
      by: 'by {organizer}',
    },
    tipTitle: 'Tip',
    tip: 'Events with clear evaluation criteria get more consistent votes.',
    errors: {
      generic: 'We couldn\'t create the event. Please try again.',
    },
    cancel: 'Cancel',
    back: '← Back',
    nextCapacity: 'Next: Dates and capacity →',
    nextReview: 'Next: Review →',
    submit: 'Create event',
    submitting: 'Creating event…',
    saveDraft: 'Save draft',
    draftSaved: 'Draft saved.',
    draftSaving: 'Saving…',
    draftAutosaved: 'Draft saved {age} ago',
    draftAgeSeconds: '{count} s',
    draftAgeMinutes: '{count} min',
    draftAgeHours: '{count} h',
    discard: {
      title: 'Discard the event?',
      text: 'What you entered will be lost.',
      keep: 'Keep editing',
      confirm: 'Discard',
      close: 'Close',
    },
  },
  editEvent: {
    title: 'Edit event details',
    close: 'Close',
    cancel: 'Cancel',
    save: 'Save changes',
    saving: 'Saving…',
    errors: {
      generic: 'We couldn\'t save the changes. Please try again.',
      stageLocked: 'The event has moved to voting and its details can no longer be edited.',
    },
    discard: {
      title: 'Discard the changes?',
      keep: 'Keep editing',
      confirm: 'Discard',
    },
  },
  manageEvent: {
    created: 'Event created. When it\'s ready, open registration.',
    updated: 'Details updated.',
  },
  manage: {
    back: '← My events',
    aside: 'Side panel',
    role: 'You organize this event',
    meta: '{organizer} · created on {date}',
    editData: 'Edit details',
    copyInvite: 'Copy invitation link',
    copied: '✓ Copied',
    share: 'Share',
    loading: 'Loading management…',
    loadError: 'We couldn\'t load the event.',
    goToEvents: 'Go to Events',
    forbidden: {
      title: 'You don\'t have permission to manage this event',
      text: 'Only the person who created the event can manage it.',
    },
    pill: {
      draft: 'Draft · not visible',
      open: 'Registration open',
      openCloses: plural({ one: 'Registration open · closes in {count} day', other: 'Registration open · closes in {count} days' }),
      openClosesToday: 'Registration open · closes today',
      voting: 'Voting in progress',
      votingCloses: plural({ one: 'Voting in progress · closes in {count} day', other: 'Voting in progress · closes in {count} days' }),
      votingClosesToday: 'Voting in progress · closes today',
      results: 'Finished · results published',
      paused: 'Paused',
      cancelled: 'Cancelled',
    },
    timeline: {
      creationNow: 'You set up the event',
      noDeadline: 'No closing date',
      closes: 'Closes on {date}',
      files: plural({ one: '{count} file received', other: '{count} files received' }),
      published: 'Published',
      edit: 'edit',
    },
    metrics: {
      registered: 'Registered',
      registeredA11y: 'Registered: {count} of {max}',
      files: 'Files received',
      filesA11y: 'Files received: {count} of {total}',
      rankings: 'Rankings sent',
      rankingsValue: '{sent} of {total}',
      rankingsA11y: 'Rankings sent: {sent} of {total}',
      deadline: 'Closing',
      days: plural({ one: '{count} day', other: '{count} days' }),
      closesToday: 'Closes today',
      noDeadline: 'Not set',
    },
    participants: {
      title: 'Participants',
      titleWithCount: 'Participants · {count} / {max}',
      caption: 'Event participants',
      name: 'Name',
      email: 'Email',
      file: 'File',
      registeredAt: 'Registered',
      vote: 'Vote',
      fileMissing: 'File missing',
      download: 'Download {name}',
      voteSent: '✓ Sent',
      votePending: 'Pending',
      voteNotParticipating: 'Not participating',
      error: 'We couldn\'t load the participants.',
      statsError: 'We couldn\'t load the voting statistics.',
      downloadError: 'We couldn\'t download "{name}". Try again.',
    },
    empty: {
      title: 'No one has registered yet',
      creation: 'Once you open registration you\'ll be able to share the link.',
      participation: 'Share the invitation link to bring in participants.',
    },
    reminder: {
      file: plural({ one: 'Remind those who haven\'t uploaded a file ({count})', other: 'Remind those who haven\'t uploaded a file ({count})' }),
      vote: plural({ one: 'Send a reminder to {count} pending person', other: 'Send a reminder to {count} pending people' }),
      sent: 'Reminder sent',
    },
    nextStep: {
      eyebrow: 'Next step',
      creation: {
        title: 'Open registration',
        text: 'While it\'s in setup, no one can see or join the event. Check that everything is ready:',
        action: 'Open registration →',
      },
      checklist: {
        details: 'Name and description',
        capacity: 'Capacity: {count} participants',
        deadline: 'Registration closing date (you set it when opening)',
      },
      participation: {
        title: 'Move to voting',
        text: 'Registration closes and every participant with a proposal receives others\' proposals to review.',
        action: 'Set up and open voting →',
      },
      voting: {
        title: 'Publish results',
        text: 'Recommended once everyone has voted or when the closing date arrives ({date}).',
        textNoDate: 'Recommended once everyone has voted or when the closing date arrives.',
        action: 'Close voting and publish',
      },
    },
    consequence: {
      missingFiles: plural({ one: '{count} participant hasn\'t uploaded their file yet and won\'t review or be reviewed.', other: '{count} participants haven\'t uploaded their file yet and won\'t review or be reviewed.' }),
      minimum: 'At least 3 participants with a proposal are needed to open voting. Right now there are {count}.',
      missingRankings: '{missing} of {total} rankings are missing.',
    },
    config: {
      title: 'Applied settings',
      text: plural({ one: '{count} proposal per reviewer · at least {min} reviews per file · {quality} quality settings', other: '{count} proposals per reviewer · at least {min} reviews per file · {quality} quality settings' }),
      recommended: 'recommended',
      custom: 'custom',
      error: 'We couldn\'t load the applied settings.',
    },
    summary: {
      title: 'Summary',
      participants: 'Participants',
      participantsValue: '0 / {max}',
      files: 'Files',
      deadline: 'Registration closes',
      notSet: 'Not set',
    },
    after: {
      title: 'Next',
      text: 'Participants register and upload their file until the closing date you set.',
    },
    countdown: {
      registration: {
        title: 'REGISTRATION CLOSES IN',
        expired: 'REGISTRATION HAS CLOSED',
        days: 'DAYS',
        hours: 'HOURS',
        minutes: 'MIN',
        seconds: 'SEC',
        footer: '{date} · 23:59 (ART)',
      },
      voting: {
        title: 'VOTING CLOSES IN',
        expired: 'VOTING HAS CLOSED',
        days: 'DAYS',
        hours: 'HOURS',
        minutes: 'MIN',
        seconds: 'SEC',
        footer: '{date} · 23:59 (ART)',
      },
    },
    pause: {
      action: 'Pause event',
      resume: 'Resume event',
      confirmTitle: 'Pause the event?',
      confirmText: 'While it\'s paused, no one can register, upload proposals or vote.',
      cancel: 'Cancel',
      error: 'We couldn\'t change the event\'s status. Try again.',
    },
    pausedNotice: 'The event is paused. Participants can\'t register, upload proposals or vote until you resume it.',
    success: {
      registrationOpened: 'Registration is open. Share the link to bring in participants.',
      votingOpened: 'Voting is open. Proposals have been assigned.',
      resultsPublished: 'Results published.',
      reminderSent: plural({ one: 'Reminder sent to {count} participant.', other: 'Reminder sent to {count} participants.' }),
      deadlineUpdated: 'Closing date updated.',
    },
    dialog: {
      close: 'Close',
      cancel: 'Cancel',
      presets: {
        '3': '3 days',
        '7': '1 week',
        '14': '2 weeks',
      },
      postponePresets: {
        '3': '+3 days',
        '7': '+1 week',
        '14': '+2 weeks',
      },
      change: 'Change',
      dateNotAfterToday: 'Choose a date after today.',
    },
    openRegistration: {
      eyebrow: 'Setup → Participation',
      title: 'Open registration',
      text: 'The event becomes public. People will be able to register and upload their file.',
      dateLabel: 'Until when can people register?',
      help: 'Participants will see it. You can postpone it later.',
      error: 'We couldn\'t open registration. Try again.',
      confirm: 'Open registration',
      loading: 'Opening…',
    },
    openVoting: {
      eyebrow: 'Participation → Voting',
      title: 'Open voting',
      text: 'Registration closes and the system distributes the proposals among the participants who uploaded theirs. No one reviews their own file.',
      proposals: 'Proposals',
      reviewers: 'Reviewers',
      perFile: 'Reviews per file',
      leftOut: plural({ one: '{count} registered person didn\'t upload a proposal: they won\'t review or be reviewed.', other: '{count} registered people didn\'t upload a proposal: they won\'t review or be reviewed.' }),
      minimum: 'At least 3 participants with a proposal are needed to open voting. Right now there are {count}.',
      dateLabel: 'Voting closes',
      perReviewer: 'Proposals per reviewer',
      perReviewerHelp: 'Recommended: {recommended} · maximum {max}',
      decrement: 'One fewer proposal per reviewer',
      increment: 'One more proposal per reviewer',
      advanced: 'Advanced quality settings',
      advancedHint: 'Recommended values · they work well for most events',
      advancedShow: 'Show',
      advancedHide: 'Hide',
      minEvaluations: 'Minimum reviews per file',
      adjustment: 'Quality adjustment weight',
      adjustmentUnit: 'positions',
      adjustmentRange: 'Choose a value between 1 and 10.',
      goodThreshold: 'Reliable reviewer threshold',
      goodThresholdHelp: 'Above it, their vote weighs more.',
      badThreshold: 'Unreliable reviewer threshold',
      badThresholdHelp: 'Below it, their vote weighs less.',
      thresholdError: 'The reliable threshold must exceed the unreliable one by at least 0.1.',
      stepperDecrement: 'Decrease {label}',
      stepperIncrement: 'Increase {label}',
      irreversible: 'You can\'t go back to Participation.',
      error: 'We couldn\'t open voting. The event is still in Participation; try again.',
      previewLoading: 'Calculating the distribution…',
      previewError: 'We couldn\'t calculate the distribution.',
      confirm: 'Open voting and assign',
      loading: 'Opening voting…',
    },
    publish: {
      eyebrow: 'Voting → Results',
      title: 'Close voting and publish?',
      text: 'The final ranking is calculated and becomes visible to everyone. No one will be able to vote afterwards.',
      missingTitle: '{missing} of {total} rankings are missing',
      missingText: 'With few votes the result is less reliable. Anyone who didn\'t send their ranking gets a quality of 0 and their proposal moves down.',
      error: 'We couldn\'t publish the results. Try again.',
      wait: 'Keep waiting',
      confirmAnyway: 'Publish anyway',
      confirm: 'Publish results',
      loading: 'Publishing…',
    },
    reminderDialog: {
      fileTitle: 'Remind them the file is missing',
      voteTitle: 'Remind them the ranking is missing',
      text: plural({ one: '{count} participant will get an email and an in-app notification with the stage\'s closing date.', other: '{count} participants will get an email and an in-app notification with the stage\'s closing date.' }),
      more: plural({ one: 'and {count} more', other: 'and {count} more' }),
      none: 'There\'s no one pending anymore.',
      error: 'We couldn\'t send the reminder. Try again.',
      paused: 'The event is paused; resume it to send reminders.',
      confirm: 'Send reminder',
      loading: 'Sending…',
    },
    deadline: {
      eyebrowParticipation: 'Participation',
      eyebrowVoting: 'Voting',
      title: 'Postpone the closing date',
      current: 'Current closing date: {date}',
      label: 'New closing date',
      help: 'The closing date can only be postponed. Participants get an email and a notification with the new date.',
      notPostponed: 'The closing date can only be postponed. Choose a date after {date}.',
      error: 'We couldn\'t change the closing date. Try again.',
      confirm: 'Postpone closing date',
      loading: 'Saving…',
    },
  },
  ranking: {
    proposal: 'Proposal {number}',
    meta: '{type} · {size}',
    viewFile: 'View file',
    viewFileAccessible: 'View file of {proposal}',
    best: 'Best',
    middle: 'Middle',
    worst: 'Least',
    moveUp: 'Move up {proposal}',
    moveDown: 'Move down {proposal}',
    moved: '{proposal} moved to position {position}',
    listLabel: 'Your proposal ranking',
    readonlyTitle: 'Your ranking',
  },
  eventDetail: {
    back: '← Events',
    share: 'Share',
    meta: 'Organized by {organizer} · {count} of {max} participants',
    metaFinished: 'Ended on {date}',
    loading: 'Loading event…',
    loadError: "We couldn't load the event.",
    goToEvents: 'Go to Events',
    pill: {
      open: 'Registration open',
      openCloses: plural({ one: 'Registration open · closes in {count} day', other: 'Registration open · closes in {count} days' }),
      openClosesToday: 'Registration open · closes today',
      missingFile: 'Registered · your file is missing',
      proposalSent: 'Proposal sent',
      vote: 'Your turn to vote',
      voteCloses: plural({ one: 'Your turn to vote · closes in {count} day', other: 'Your turn to vote · closes in {count} days' }),
      voteClosesToday: 'Your turn to vote · closes today',
      rankingSent: 'Ranking sent',
      votingInProgress: 'Voting in progress',
      results: 'Finished · results published',
      paused: 'Event paused',
      cancelled: 'Cancelled',
    },
    timeline: {
      creation: 'Event set up',
      participation: 'Sign up and upload your file',
      voting: 'You review other participants',
      results: 'Final ranking',
      closes: 'Closes on {date}',
    },
    nextStep: {
      eyebrow: 'Your next step',
      default: {
        title: 'Sign up to take part',
        text: 'After signing up you can upload your proposal. You can replace it until the deadline.',
      },
      upload: {
        title: 'Upload your proposal',
        text: 'You can replace it as many times as you want until {date}.',
        textNoDate: 'You can replace it as many times as you want until the deadline.',
      },
      submitted: {
        title: 'Your proposal is sent',
        text: 'You can replace it until {date}.',
        textNoDate: 'You can replace it until the deadline.',
      },
      vote: {
        title: plural({ one: 'Rank the {count} proposal', other: 'Rank the {count} proposals' }),
        text: 'Put the one you think is best at the top. Open each file before deciding.',
      },
      rankingSent: {
        title: 'Your ranking is sent',
        text: 'You can change it and send it again until voting closes.',
      },
      paused: {
        title: 'The event is paused',
        text: "For now you can't sign up or upload proposals. The organizer will resume the event.",
      },
      full: {
        title: 'The event is full',
        text: 'There are no places left in this event.',
      },
      noProposalInVoting: {
        title: "You're not part of this vote",
        text: "You didn't upload a proposal during registration, so in this event you don't review or get reviewed.",
      },
      votingNotRegistered: {
        title: 'Voting is in progress',
        text: 'Participants are reviewing the proposals. Results are published when voting closes.',
      },
      results: {
        title: 'This is how the community voted',
        text: '{participants} · {evaluations}',
      },
      cancelled: {
        title: 'This event was cancelled',
        text: 'You can no longer take part in this event.',
      },
    },
    requirements: {
      label: 'Requirements',
      formats: 'Image or document',
      size: 'Max. 10 MB',
      closes: 'Deadline: {date}',
    },
    register: 'Sign up for the event',
    registering: 'Signing up…',
    upload: {
      prompt: 'Drag your file here or',
      promptAction: 'choose it from your device',
      formats: 'JPG, PNG, GIF, WebP, PDF, TXT, DOC, DOCX · up to 10 MB',
      ready: 'ready to send',
      change: 'Change',
      changeAccessible: 'Change file {name}',
      submittedOn: 'Sent on {date}',
      replace: 'Replace file',
      tooLarge: 'The file is larger than 10 MB.',
      invalidType: "That format isn't allowed. Use JPG, PNG, GIF, WebP, PDF, TXT, DOC or DOCX.",
      multiple: 'Choose a single file.',
    },
    comment: {
      label: 'Comment',
      optional: '(optional)',
      help: 'Reviewers will see it next to your file.',
    },
    submit: 'Send proposal',
    submitReplace: 'Send new version',
    submitting: 'Sending proposal…',
    submitHelpEmpty: 'Choose a file to continue',
    submitHelpReady: 'You can replace it until the deadline',
    received: 'We received your proposal.',
    errors: {
      register: "We couldn't sign you up. Please try again.",
      upload: "We couldn't send your proposal. Please try again.",
      replace: "We couldn't replace your proposal. Please try again.",
      assignment: "We couldn't load your assigned proposals.",
      openFile: "We couldn't open the file. Please try again.",
      submitRanking: "We couldn't send your ranking. Please try again.",
      saveDraft: "We couldn't save the draft. Your changes are still on screen.",
      votingClosed: 'Voting has closed. The ranking can no longer be changed.',
    },
    ranking: {
      submit: 'Send my ranking',
      resubmit: 'Resend my ranking',
      submitting: 'Sending ranking…',
      help: 'You can change it until voting closes.',
      draftSaved: 'Draft saved',
    },
    rankingReceived: 'We received your ranking.',
    progress: {
      title: 'Your progress',
      done: 'Completed',
      registration: 'Registration',
      confirmed: 'Confirmed',
      upload: 'Upload proposal',
      pending: 'Pending',
      pendingCloses: plural({ one: 'Pending · closes in {count} day', other: 'Pending · closes in {count} days' }),
      sent: 'Sent',
      vote: 'Vote',
      voteLater: "We'll let you know when it starts",
      voteSent: 'Sent',
      results: 'See results',
    },
    howVoteCounts: {
      title: 'How does your vote count?',
      text: "Your order is compared with the other reviewers'. If you review consistently, your own proposal moves up in the final ranking; if not, it moves down.",
    },
    about: 'About the event',
    details: {
      title: 'Details',
      organizer: 'Organizer',
      closes: '{stage} closes',
      participants: 'Participants',
      participantsValue: '{count} / {max}',
      view: 'view',
      viewAccessible: 'View participants',
      evaluations: 'Reviews',
      finished: 'Ended',
    },
    after: {
      title: 'Next',
      participation: 'When registration closes, the organizer opens voting and assigns you proposals to review.',
      voting: 'When voting closes, the final ranking is published for everyone.',
    },
    banner: {
      title: 'Did you like it?',
      text: plural({ one: 'There is {count} event open for registration now.', other: 'There are {count} events open for registration now.' }),
      guest: 'Create your account to take part in the next one.',
      cta: 'See events',
      createAccount: 'Create account',
    },
  },
  share: {
    eyebrow: 'Share',
    close: 'Close',
    audience: {
      participation: 'Anyone who opens the link will see the event and can sign up.',
      voting: 'Anyone who opens the link will see the event.',
      results: 'Anyone who opens the link will see the results, even without an account.',
    },
    linkLabel: 'Event link',
    copy: 'Copy',
    copied: '✓ Copied',
    copyFailed: "We couldn't copy the link. Select it and copy it manually.",
    via: 'or send it via',
    networks: {
      whatsapp: 'WhatsApp',
      x: 'X',
      linkedin: 'LinkedIn',
      facebook: 'Facebook',
      email: 'Email',
    },
    networkAccessible: 'Share via {network}',
    more: 'More options',
  },
  participants: {
    title: 'Participants · {count} / {max}',
    close: 'Close',
    name: 'Name',
    registeredAt: 'Signed up',
    empty: 'Nobody has signed up yet.',
    error: "We couldn't load the participants.",
    loading: 'Loading participants…',
  },
  results: {
    podium: 'Podium',
    place: 'Place {position}',
    position: 'Position',
    participant: 'Participant',
    proposal: 'Proposal',
    score: 'Score',
    ranking: 'Full ranking',
    you: 'You',
    evaluations: plural({ one: '{count} review', other: '{count} reviews' }),
    note: "The score combines every participant's ranking on a 0 to 10 scale. The proposal of anyone who reviewed consistently moves up; otherwise it moves down.",
    loading: 'Loading results…',
    error: "We couldn't load the results.",
  },
  errors: {
    ALREADY_CANCELLED: 'This event has already been cancelled.',
    ALREADY_REGISTERED: 'You are already registered for this event.',
    ASSIGNMENTS_EXIST: 'The assignments for this event have already been generated.',
    ASSIGNMENT_NOT_FOUND: "We couldn't find this assignment.",
    ATTACHMENTS_ERROR: "We couldn't complete the action on the server. Please try again in a few minutes.",
    ATTACHMENT_NOT_FOUND: "We couldn't find this proposal.",
    BAD_REQUEST: "We couldn't process the request. Check the data and try again.",
    CANCEL_ERROR: "We couldn't cancel the event. Please try again.",
    CANNOT_ADVANCE_DEADLINE: 'You can only postpone the estimated closing date, not move it earlier.',
    CONFIG_EXISTS: 'The voting configuration for this event already exists.',
    CONFIG_INVALID: 'The voting configuration is not valid. Review it and try again.',
    CONFIG_LOOKUP_ERROR: "We couldn't look up the voting configuration. Please try again.",
    CONFIG_NOT_FOUND: "This event doesn't have a voting configuration yet.",
    CREATION_ERROR: "We couldn't create the event. Please try again.",
    CREATOR_CANNOT_DELETE: "The organizer can't be removed from their own event.",
    CREATOR_CANNOT_REGISTER: "The organizer can't register for their own event.",
    CREATOR_CANNOT_UPLOAD: "The organizer can't upload a proposal to their own event.",
    DB_CREATE_ERROR: "We couldn't complete the action on the server. Please try again in a few minutes.",
    DB_DELETE_ERROR: "We couldn't complete the action on the server. Please try again in a few minutes.",
    DB_ERROR: "We couldn't complete the action on the server. Please try again in a few minutes.",
    DB_SAVE_ERROR: "We couldn't complete the action on the server. Please try again in a few minutes.",
    DB_UPDATE_ERROR: "We couldn't complete the action on the server. Please try again in a few minutes.",
    DESCRIPTION_TOO_LONG: 'The description is too long. Shorten it and try again.',
    DRAFT_GET_ERROR: "We couldn't retrieve your draft. Please try again.",
    DRAFT_NOT_FOUND: "We couldn't find a saved draft.",
    DRAFT_SAVE_ERROR: "We couldn't save your draft. Please try again.",
    DUPLICATE_ATTACHMENT: 'You have already uploaded a proposal to this event.',
    DUPLICATE_EVENT_NAME: 'An event with this name already exists. Choose another one.',
    DURATION_TOO_LONG: 'The event duration is too long. Shorten it and try again.',
    DURATION_TOO_SHORT: 'The event duration is too short. Extend it and try again.',
    EMAIL_ALREADY_EXISTS: 'An account with this email already exists.',
    EVENT_CANCELLED: 'This event has been cancelled.',
    EVENT_LOOKUP_ERROR: "We couldn't look up the event. Please try again.",
    EVENT_NOT_FOUND: "We couldn't find this event.",
    EVENT_PAUSED: 'This event is paused. Try again once it resumes.',
    EVENT_PAUSED_OR_CANCELLED: 'This event is paused or has been cancelled.',
    EXPIRED_RESET_TOKEN: 'The password reset link has expired. Request a new one.',
    FILE_NOT_FOUND: "We couldn't find the file.",
    FILE_SAVE_ERROR: "We couldn't save the file. Please try again.",
    FILE_TOO_LARGE: 'The file is too large. Upload a smaller one.',
    FORBIDDEN: "You don't have permission to do this.",
    GENERATION_FAILED: "We couldn't generate the assignments. Please try again.",
    GOOGLE_API_ERROR: "We couldn't reach Google. Please try again in a few minutes.",
    INSUFFICIENT_ATTACHMENTS: 'At least 3 proposals are needed to open voting.',
    INSUFFICIENT_PARTICIPANTS: "There aren't enough participants to continue.",
    INTERNAL_ERROR: "We couldn't complete the action on the server. Please try again in a few minutes.",
    INVALID_ATTACHMENT_ID: 'The proposal identifier is not valid.',
    INVALID_CREDENTIALS: 'The email or password is incorrect.',
    INVALID_DATE_FORMAT: 'The date format is not valid.',
    INVALID_DATE_RANGE: 'The end date must be after the start date.',
    INVALID_END_DATE: 'The end date is not valid.',
    INVALID_ESTIMATED_DATE: 'The estimated closing date is not valid. Choose a date from today onward.',
    INVALID_EVENT_ID: 'The event identifier is not valid.',
    INVALID_EVENT_STAGE: "This action isn't available in the event's current stage.",
    INVALID_FILENAME: 'The file name is not valid.',
    INVALID_FILE_TYPE: "This file type isn't allowed.",
    INVALID_GOOGLE_TOKEN: "We couldn't validate your Google account. Please try again.",
    INVALID_MAX_PARTICIPANTS: 'The maximum number of participants is not valid.',
    INVALID_PARTICIPANT_ID: 'The participant identifier is not valid.',
    INVALID_PASSWORD: "The password doesn't meet the requirements. Choose a stronger one.",
    INVALID_PAYLOAD: 'The submitted data is not valid. Review it and try again.',
    INVALID_REGISTRATION_STAGE: 'This event is no longer open for registration.',
    INVALID_RESET_TOKEN: 'The password reset link is not valid. Request a new one.',
    INVALID_STAGE: 'The specified stage is not valid.',
    INVALID_STAGE_FILTER: 'The stage filter is not valid.',
    INVALID_STAGE_FOR_EDIT: "You can't edit this in the event's current stage.",
    INVALID_START_DATE: 'The start date is not valid.',
    INVALID_THRESHOLDS: 'The voting thresholds are not valid.',
    INVALID_TOKEN: 'Your session is not valid. Log in again.',
    INVALID_TRANSITION: "You can't move to that stage from the current one.",
    INVALID_UPDATE_STAGE: "You can't modify the event in its current stage.",
    MATH_CONSTRAINT_VIOLATION: "The voting parameters aren't compatible with each other. Adjust them and try again.",
    MAX_PARTICIPANTS_BELOW_REGISTERED: "The maximum can't be lower than the number of registered participants.",
    MAX_PARTICIPANTS_LIMIT_EXCEEDED: 'The maximum number of participants exceeds the allowed limit.',
    MAX_PARTICIPANTS_REACHED: 'The event is full. There are no spots left.',
    MISSING_ESTIMATED_DATE: 'Provide the estimated closing date of the stage.',
    MISSING_EVENT_ID: 'The event identifier is missing.',
    MISSING_PARAMETERS: 'Some required data is missing.',
    MISSING_VOTING_CONFIG: 'Set up voting before continuing.',
    M_EXCEEDS_EVALUABLE: 'The number of proposals to evaluate exceeds those available. Reduce that number.',
    NOTIFICATION_NOT_FOUND: "We couldn't find this notification.",
    NOT_FOUND: "We couldn't find what you were looking for.",
    NOT_REGISTERED: 'You need to register for the event first.',
    NO_ASSIGNMENT: "You don't have any proposals assigned to evaluate yet.",
    NO_ATTACHMENTS: 'There are no proposals in this event yet.',
    NO_AUTH_TOKEN: 'Log in to continue.',
    NO_FILE: 'Select a file to upload.',
    NO_PENDING_RECIPIENTS: 'There are no pending participants to send the reminder to.',
    OAUTH_ACCOUNT_NO_PASSWORD: 'This account signs in with Google. Use Google to log in.',
    PARTICIPANTS_ERROR: "We couldn't complete the action on the server. Please try again in a few minutes.",
    PARTICIPANT_CHECK_ERROR: "We couldn't complete the action on the server. Please try again in a few minutes.",
    PARTICIPANT_NOT_FOUND: "We couldn't find this participant.",
    PAST_START_DATE: "The start date can't be in the past.",
    REGISTRATION_ERROR: "We couldn't complete the registration. Please try again.",
    RESULTS_NOT_CALCULATED: "The results aren't available yet.",
    RETRIEVAL_ERROR: "We couldn't complete the action on the server. Please try again in a few minutes.",
    TOKEN_GENERATION_ERROR: "We couldn't complete the action on the server. Please try again in a few minutes.",
    UNAUTHORIZED: 'Log in to continue.',
    UNAUTHORIZED_ACCESS: "You don't have access to this resource.",
    UPDATE_ERROR: "We couldn't save the changes. Please try again.",
    USERNAME_ALREADY_EXISTS: 'That username is already taken. Choose another one.',
    USER_CREATE_ERROR: "We couldn't create your account. Please try again.",
    USER_NOT_FOUND: "We couldn't find this account.",
    VALIDATION_FAILED: 'Some of the data is not valid. Review it and try again.',
    VOTING_SETUP_ERROR: "We couldn't set up voting. Please try again.",
    generic: 'Something went wrong. Please try again.',
    network: "We couldn't reach the server. Check your connection and try again.",
  },
};
